import _ from "lodash";
import requestor from "requestor";
import React, {useEffect, useState} from "react";
import {bindActionCreators} from "redux";
import {connect} from "react-redux";
import {push} from "react-router-redux";
import {CircularProgress} from "@material-ui/core";

import setTitle from "utils/set_title";
import {isInitialised} from "utils/uninitialised";
import DataAssetsOverview from "../components/data_assets_overview";
import llmRunsFetchAction from "modules/flowmaster/actions/llm_runs_fetch";
import addTestAction from "modules/flowmaster/actions/workflow_test_add";
import updateTestAction from "modules/flowmaster/actions/workflow_test_update";
import deleteTestAction from "modules/flowmaster/actions/workflow_test_delete";
import rerunTestsAction from "modules/flowmaster/actions/llm_run_rerun_tests";
import getTestResultsAction from "modules/flowmaster/actions/workflow_test_results_fetch";
import llmRunFetchAction from "modules/flowmaster/actions/llm_run_fetch";
import {buildQuery, parseQuery} from "utils/uri";
import {ProjectId} from "common/types/project";
import {DocumentId} from "common/types/document";

function DataAssetsOverviewContainer(props) {
  const shouldRenderContainer = isInitialised([props.user]);

  const searchParams = new URLSearchParams(props.location.search);
  const documentId = searchParams.get("document");
  const projectId = searchParams.get("project");
  const from = searchParams.get("from") ?? null;
  const to = searchParams.get("to") ?? null;
  const user = searchParams.get("user") ?? null;
  const maxRuns = parseInt(searchParams.get("max_runs") ?? "100", 10);

  const [loadingRuns, setLoadingRuns] = useState(true);
  const [refreshingRuns, setRefreshingRuns] = useState(false);
  const [fetchRunsError, setFetchRunsError] = useState<string | null>(null);

  const [rerunningTests, setRerunningTests] = useState(false);
  const [refreshingRun, setRefreshingRun] = useState(false);

  const {organisationId} = props.params;
  const {selectedLlmRun} = props;

  const updateValues = (pairs: Record<string, unknown | undefined | null>) => {
    const {pathname, search} = location;
    const params = parseQuery(search);

    const newSearch = buildQuery(
      _.omit(
        {...params, ...pairs},
        Object.keys(pairs).filter(key => !pairs[key]),
      ),
    );

    if (search !== newSearch) {
      props.dispatch(push(pathname + newSearch));
    }
  };

  const filterByRunEntity = (projectId: ProjectId, documentId?: DocumentId) => {
    updateValues({
      project: projectId.toString(),
      ...(Boolean(documentId) && {document: documentId?.toString()}),
    });
  };

  const handleRefresh = () => {
    fetchRuns(true);
  };

  async function fetchRuns(refresh = false) {
    const {organisationId} = props.params;

    setFetchRunsError(null);

    if (refresh) {
      setRefreshingRuns(true);
    } else {
      setLoadingRuns(true);
    }

    try {
      await props.fetchLlmRuns(
        organisationId,
        {
          document: documentId,
          project: projectId,
          fetch_data_assets: true,
          from,
          to,
          user,
        },
        maxRuns,
      );
    } catch (error) {
      setFetchRunsError("Failed to load runs");
    } finally {
      setRefreshingRuns(false);
      setLoadingRuns(false);
    }
  }

  const addTestHandler = (dataKey, key) => {
    const nameKey = selectedLlmRun?.name ?? null;
    props.addTest(
      organisationId,
      selectedLlmRun.workflow_context_id,
      selectedLlmRun.links,
      dataKey,
      key,
      nameKey,
    );
  };

  const updateTestHandler = (testId, testType, value) => {
    props.updateTest(
      organisationId,
      selectedLlmRun.workflow_context_id,
      testId,
      testType,
      value,
    );
  };

  const deleteTestHandler = testId => {
    props.deleteTest(
      organisationId,
      selectedLlmRun.workflow_context_id,
      testId,
    );
  };

  const rerunTestsHandler = async runId => {
    setRerunningTests(true);
    try {
      await props.rerunTests(organisationId, runId);
    } finally {
      setRerunningTests(false);
    }

    refreshRunHandler(organisationId, runId);
  };

  const refreshRunHandler = async (organisationId, runId) => {
    setRefreshingRun(true);
    try {
      await props.fetchLlmRun(organisationId, runId);
    } finally {
      setRefreshingRun(false);
    }
  };

  const getTestResultsHandler = (dataKey, key) => {
    props.getTestResults(organisationId, selectedLlmRun.id, dataKey, key);
  };

  useEffect(() => {
    if (shouldRenderContainer && props.user?.permissions) {
      fetchRuns();
    }
  }, [shouldRenderContainer, maxRuns]);

  if (!shouldRenderContainer || !props.user.permissions) {
    return (
      <div
        style={{
          flexGrow: 1,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
        }}
      >
        <CircularProgress />
      </div>
    );
  }

  setTitle("Data Assets");
  return (
    <DataAssetsOverview
      {...{
        location: props.location,
        isLoading: loadingRuns,
        isRefreshing: refreshingRuns,
        dataAssetRuns: props.dataAssetRuns,
        fetchRuns,
        fetchRunsError,
        fetchLlmRun: refreshRunHandler,
        selectedLlmRun,
        organisationId: props.organisationId,
        onAddTest: addTestHandler,
        onUpdateTest: updateTestHandler,
        onDeleteTest: deleteTestHandler,
        rerunTests: rerunTestsHandler,
        getTestResults: getTestResultsHandler,
        filterByRunEntity,
        updateFilterValue: updateValues,
        definitions: props.definitions,
        onRefreshClick: handleRefresh,
        isRefreshingRun: refreshingRun,
        isRerunningTests: rerunningTests,
      }}
    />
  );
}

function mapDispatchToProps(dispatch) {
  return {
    dispatch,
    ...bindActionCreators(
      {
        push,
        fetchLlmRuns: llmRunsFetchAction(requestor),
        fetchLlmRun: llmRunFetchAction(requestor),
        addTest: addTestAction(requestor),
        updateTest: updateTestAction(requestor),
        deleteTest: deleteTestAction(requestor),
        rerunTests: rerunTestsAction(requestor),
        getTestResults: getTestResultsAction(requestor),
      },
      dispatch,
    ),
  };
}

function select(state) {
  return {
    organisation: state.organisation,
    user: state.user,
    dataAssetRuns: state.llmRuns,
    organisationId: state.organisation.id,
    selectedLlmRun: state.llmRun,
    definitions: state.workflows,
  };
}

export default connect(select, mapDispatchToProps)(DataAssetsOverviewContainer);
